import { slugify } from "./slug.js";

const H2 = /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi;
const TAG = /<[^>]*>/g;

const ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

const decodeEntities = (text: string): string =>
  text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (match, code: string) => {
    if (code[0] === "#") {
      const n = code[1] === "x" || code[1] === "X" ? parseInt(code.slice(2), 16) : Number(code.slice(1));
      return Number.isFinite(n) ? String.fromCodePoint(n) : match;
    }
    return ENTITIES[code.toLowerCase()] ?? match;
  });

/**
 * Texto visível de um `<h2>`: sem tags e com entidades resolvidas — é o que o
 * leitor vê e o que vira âncora.
 */
const headingText = (innerHtml: string): string =>
  decodeEntities(innerHtml.replace(TAG, "")).replace(/\s+/g, " ").trim();

/**
 * Grava `id` em cada `<h2>` do HTML **já sanitizado** — a âncora do índice
 * "Neste artigo".
 *
 * Por que no HTML e não no DOM: o índice escrevia o `id` direto nos `h2`
 * depois da montagem, e bastava o React reaplicar o `innerHTML` do corpo (o
 * clique numa âncora faz isso) para os `h2` virarem nós novos, sem `id` — o
 * link mudava a URL e não rolava. No HTML, a âncora sobrevive a qualquer
 * re-render, existe no SSR (o crawler e o link direto `/blog/x#secao` passam
 * a funcionar) e cliente e servidor produzem a mesma marcação.
 *
 * O sanitizador continua sem permitir `id` vindo do editor: o valor aqui é
 * gerado por nós e só contém `[a-z0-9-]`, então não há o que escapar.
 * Títulos repetidos recebem sufixo (`-2`, `-3`) para o segundo não levar ao
 * primeiro.
 */
export const addHeadingIds = (html: string): string => {
  const used = new Set<string>();

  return html.replace(H2, (match, attrs: string | undefined, inner: string) => {
    if (attrs && /\sid\s*=/i.test(attrs)) return match;

    const text = headingText(inner);
    if (!text) return match;

    const base = slugify(text) || "secao";
    let id = base;
    let n = 2;
    while (used.has(id)) id = `${base}-${n++}`;
    used.add(id);

    return `<h2 id="${id}"${attrs ?? ""}>${inner}</h2>`;
  });
};
